/**
 * Clipboard Service - Handles copying text to clipboard with multiple fallback methods
 * Addresses Permissions Policy violations on Vercel and other hosting platforms
 */

class ClipboardService {
  /**
   * Attempts to copy text to clipboard using multiple methods
   * @param {string} text - The text to copy
   * @returns {Promise<boolean>} - True if successful, false otherwise
   */
  static async copyToClipboard(text) {
    // Prefer the modern Clipboard API first when available; fall back if it fails
    if (await this.tryModernClipboardAPI(text)) {
      return true;
    }

    // Method 2: Legacy document.execCommand (fallbacks)
    if (await this.tryLegacyClipboardAPI(text)) {
      return true;
    }

    // Method 3: Create temporary input element
    if (await this.tryTemporaryInputElement(text)) {
      return true;
    }

    // Method 4: Show text in alert for manual copy
    this.showManualCopyAlert(text);
    return false;
  }

  /**
   * Check if clipboard is restricted by permissions policy
   */
  static isClipboardRestricted() {
    // Check if we're on a hosting platform that might block clipboard
    const hostname = window.location.hostname;
    const isLimitedHosting = hostname.includes('vercel.app') || hostname.includes('vercel.com') || 
                            hostname.includes('netlify.app') || hostname.includes('github.io');
    
    // Use secure-context detection which treats localhost as secure in modern browsers
    const isSecure = window.isSecureContext === true;
    
    // Check if clipboard API is available
    const hasClipboardAPI = !!(navigator.clipboard && navigator.clipboard.writeText);
    
    // If we're on limited hosting or don't have secure context or API, assume restricted
    if (isLimitedHosting || !isSecure || !hasClipboardAPI) {
      return true;
    }
    
    return false;
  }

  /**
   * Try modern Clipboard API
   */
  static async tryModernClipboardAPI(text) {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (error) {
      console.warn('Modern Clipboard API failed:', error.message);
    }
    return false;
  }

  /**
   * Try legacy document.execCommand method
   */
  static async tryLegacyClipboardAPI(text) {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      return successful;
    } catch (error) {
      console.warn('Legacy Clipboard API failed:', error.message);
    }
    return false;
  }

  /**
   * Try creating a temporary input element
   */
  static async tryTemporaryInputElement(text) {
    try {
      const input = document.createElement('input');
      input.type = 'text';
      input.value = text;
      input.style.position = 'absolute';
      input.style.left = '-9999px';
      document.body.appendChild(input);
      
      input.select();
      input.setSelectionRange(0, 99999); // For mobile devices
      
      const successful = document.execCommand('copy');
      document.body.removeChild(input);
      
      return successful;
    } catch (error) {
      console.warn('Temporary input element method failed:', error.message);
    }
    return false;
  }

  /**
   * Show manual copy alert as last resort
   */
  static showManualCopyAlert(text) {
    const message = `Please copy this link manually:\n\n${text}`;
    
    // Try to show in a more user-friendly way
    if (window.confirm('Clipboard access is blocked. Would you like to see the link to copy manually?')) {
      // Create a modal or use prompt
      const userInput = prompt('Copy this link:', text);
      if (userInput === text) {
        alert('Link copied successfully!');
      }
    }
  }

  /**
   * Check if clipboard API is available
   */
  static isClipboardSupported() {
    return !!(navigator.clipboard && navigator.clipboard.writeText);
  }

  /**
   * Get clipboard status for debugging
   */
  static getClipboardStatus() {
    return {
      modernAPI: !!(navigator.clipboard && navigator.clipboard.writeText),
      userAgent: navigator.userAgent,
      protocol: window.location.protocol,
      hostname: window.location.hostname,
      permissionsPolicy: this.getPermissionsPolicyInfo()
    };
  }

  /**
   * Get permissions policy information
   */
  static getPermissionsPolicyInfo() {
    try {
      // Check if Permissions-Policy header is present
      const policy = document.querySelector('meta[http-equiv="Permissions-Policy"]');
      return {
        hasPolicyHeader: !!policy,
        policyContent: policy ? policy.getAttribute('content') : null
      };
    } catch (error) {
      return { error: error.message };
    }
  }
}

export default ClipboardService;
